// Sværhedstabellen. Alle parametre for de fire niveauer står her og kun her (DESIGN.md "Sværhedsgrader").

import type { ActionKind, DifficultyDef, DifficultyId } from './types';

export const DIFFICULTIES: Record<DifficultyId, DifficultyDef> = {
  laerling: {
    id: 'laerling',
    name: 'Lærling',
    demandGrowth: 1.0,
    customersPerMin: 3,
    eventBudgetT0: 2,
    eventBudgetK: 0.5,
    maxConcurrent: 1,
    warnS: 15,
    // IGN-løkke runde 1: "Lærling er triviel, og der mangler et mellemtrin". 24 s giver stadig god tid,
    // men en by, man glemmer, mærkes nu inden for årstiden.
    clockS: 24,
    clockFloorPm: 200,
    trust: 3,
    startBudgetFactor: 1.0,
    phantomPct: 0,
    stormLines: 1,
    unwarnedDip: 'no',
    n1: 'shield',
    greenGoalDeltaPp: -10,
    autopause: 'yes',
    buildInPause: true,
    // Co-op runde 2: "Lærling-co-op tabes på 3-4 minutter, selv når planlæggeren følger hints". To
    // mennesker på Lærling får samme net som én; makkerspillet er sværere fra Vagthavende.
    coopDemand: 1.0,
    coopConcurrent: 0,
    calmYears: 2,
    // IGN-løkke 2, runde 2: "bane 1 er sværest; en pilot tabte den i år 3 med 79 kr ubrugt". Første
    // bane er der, mobilspillere falder fra, så på Lærling bliver det sidste hjerte der.
    spareLevels: 1,
    // IGN-løkke 2, runde 3 (mobil): bane 4 tabtes to gange på et datacenter. Fra bane 4 er banens
    // første udfald på huset; spillet siger det højt, så det lærer i stedet for at straffe.
    graceFirst: { fromLevel: 4 },
    stormFreeYears: 0,
    advanceShare: 1,
    levelRamp: 0,
    // Spor "sværhed": eksperten sluttede Lærling med 100-137 kr ubrugt, for intet var værd at købe.
    // Indtægten strammes fra bane 1 (75 %, derefter 50 %), og nybegynderen vinder stadig det hele.
    cashRamp: 0.25,
    cashRampFrom: 0,
    blackoutGapS: 15,
    growthEveryS: 30,
    pullAheadS: 8,
    frontGrant: true,
    startGraceS: 20,
    firstYearGraceS: 15,
    coach: 'full',
    coachFade: { fromLevel: 3, mode: 'urgent' },
    repairKr: 5,
  },
  vagthavende: {
    id: 'vagthavende',
    name: 'Vagthavende',
    demandGrowth: 1.0,
    customersPerMin: 4,
    eventBudgetT0: 3.5,
    // Spor "sværhed": "Vagthavende er en søndagstur" (alle vandt). Flere hændelser, op til tre ad
    // gangen, kortere varsel og 8 s mørkeur. Nybegynderen vinder nu omkring halvdelen.
    eventBudgetK: 2.5,
    maxConcurrent: 3,
    warnS: 8,
    clockS: 8,
    clockFloorPm: 400,
    trust: 2,
    startBudgetFactor: 1.0,
    phantomPct: 10,
    stormLines: 2,
    unwarnedDip: 'vulnerable',
    n1: 'bonus',
    greenGoalDeltaPp: 0,
    autopause: 'optional',
    buildInPause: true,
    coopDemand: 1.2,
    coopConcurrent: 1,
    calmYears: 0,
    stormFreeYears: 0,
    advanceShare: 0.5,
    levelRamp: 0,
    cashRamp: 0.2,
    cashRampFrom: 1,
    blackoutGapS: 15,
    growthEveryS: 20,
    pullAheadS: 8,
    frontGrant: true,
    startGraceS: 20,
    firstYearGraceS: 15,
    coach: 'urgent',
    repairKr: 5,
  },
  systemansvarlig: {
    id: 'systemansvarlig',
    name: 'Systemansvarlig',
    demandGrowth: 1.0,
    customersPerMin: 4,
    eventBudgetT0: 5,
    // Spor "sværhed": kortere varsel og ur og flere hændelser, så færdighed skiller gennemsnit fra ekspert.
    eventBudgetK: 3.5,
    maxConcurrent: 3,
    warnS: 4,
    clockS: 4,
    clockFloorPm: 500,
    // IGN-løkke runde 1: 10 s frist tog banen fra spilleren, før hun havde set nettet. Nu længere frist
    // ved start og byg i pause; to hjerter består, for med tre vandt den gennemsnitlige bot lige så
    // ofte og næsten lige så flot som eksperten (balance: 2,50★ mod 2,58★), og så var færdighed ligegyldig.
    trust: 2,
    // Playtest runde 3 hævede startkassen til 45 kr for de to milde niveauer; her bliver den på 40 kr.
    startBudgetFactor: 0.89,
    phantomPct: 20,
    stormLines: 2,
    unwarnedDip: 'yes',
    n1: 'required',
    greenGoalDeltaPp: 2,
    autopause: 'no',
    // Playtest runde 3 slog byg i pause fra; IGN-løkke runde 1 slog det til igen: anmelderne kaldte
    // det urimeligt, når kortene og byerne ikke ventede. Orkan er nu den eneste uden byg i pause.
    buildInPause: true,
    coopDemand: 1.4,
    coopConcurrent: 1,
    calmYears: 0,
    stormFreeYears: 0,
    advanceShare: 0.4,
    levelRamp: 0,
    // Spor "sværhed": gennemsnittet vandt bane 3 og 4 næsten altid (95 %), og eksperten sluttede med
    // penge, hun ikke kunne bruge. Indtægten strammes fra bane 2 (85 %, 70 %, 55 %).
    cashRamp: 0.15,
    cashRampFrom: 1,
    growthEveryS: 14,
    startGraceS: 15,
    firstYearGraceS: 12,
    coach: 'ring',
    // IGN-løkke 2, runde 3: ingen nåde i år 0 her. Med den vandt Gennemsnit 75 % (mål 35-70 %);
    // Systemansvarlig skal stadig kunne tabes på det første uvejr.
  },
  orkan: {
    id: 'orkan',
    name: 'Orkan',
    demandGrowth: 1.0,
    customersPerMin: 5,
    eventBudgetT0: 6,
    // Spor "sværhed": Orkan blev nem, da hver banes første år fik ring (firstYearCoach). Resten af
    // banen strammes: kortere varsel og ur, flere hændelser og hurtigere skærpelse bane for bane.
    eventBudgetK: 3.5,
    maxConcurrent: 4,
    warnS: 3,
    // 3 s mørkeur: én by, man ikke når, er et hjerte. Det er mesterkravet, ikke en mur, for ringen
    // viser vej i første år, og fristen ved start og nytår gælder stadig.
    clockS: 3,
    clockFloorPm: 500,
    trust: 2,
    startBudgetFactor: 0.72,
    phantomPct: 30,
    stormLines: 3,
    unwarnedDip: 'yes_n2',
    n1: 'cascades',
    greenGoalDeltaPp: 4,
    autopause: 'no',
    buildInPause: false,
    coopDemand: 1.4,
    coopConcurrent: 1,
    calmYears: 0,
    stormFreeYears: 0,
    advanceShare: 0.25,
    levelRamp: 0.5,
    // Kassen strammes fra bane 3 (85 %, derefter 70 %), så de sene baner ikke kan købes fri.
    cashRamp: 0.15,
    cashRampFrom: 2,
    growthEveryS: 15,
    // IGN-løkke runde 2: "Orkans første minut er en mur". Lidt længere frist fra start og for de
    // første nye byer; resten af banen er lige så hård som før.
    startGraceS: 16,
    firstYearGraceS: 12,
    // IGN-løkke 2: "Orkan er en mur". En ny by midt i banen fik 10 s og 3 s mørkeur, så den kunne gå
    // ud, mens man stod i en storm andetsteds. 14 s er nok til at se den og trække en ledning.
    laterGraceS: 14,
    // Spor "sværhed": Orkans første minut skal kunne læres. Ringen viser det akutte i hver banes
    // første år; derefter er man alene.
    coach: 'off',
    firstYearCoach: 'ring',
    // IGN-løkke 2, runde 3: "Orkan er en mur; ét uvejr tager begge hjerter i år 0". Første udfald i
    // år 0 er på huset, når et brud skar byen fra, så banen tabes på beslutninger og ikke på det
    // første uvejr. Et udfald af for lidt strøm eller for små ledninger koster stadig.
    graceFirst: { untilYear: 0, causes: ['cut'] },
  },
};

export const DIFFICULTY_ORDER: DifficultyId[] = ['laerling', 'vagthavende', 'systemansvarlig', 'orkan'];

/**
 * Handlinger, der altid må gøres i pause: gaven, et svar på et hændelseskort og at sende en montør.
 * IGN-løkke runde 1: "kortets knapper gør intet i pause" føltes som en fejl, ikke som en regel.
 */
const PAUSE_ALWAYS: ReadonlySet<ActionKind> = new Set<ActionKind>(['pick_pack', 'pick_both', 'wait', 'answer_card', 'dispatch_crew']);

/** Må handlingen altid gøres, også når uret står stille (gave, kort, montør)? */
export function alwaysInPause(kind: ActionKind): boolean {
  return PAUSE_ALWAYS.has(kind);
}

/** Må handlingen gøres, mens spillet holder pause? Resten følger sværhedens buildInPause. */
export function allowedInPause(kind: ActionKind, d: DifficultyDef): boolean {
  return PAUSE_ALWAYS.has(kind) || d.buildInPause;
}

export function difficulty(id: DifficultyId): DifficultyDef {
  return DIFFICULTIES[id];
}

/** Faktoren på indtægt og væksttilskud for banen (se cashRamp). */
export function cashFactorOf(d: DifficultyDef, levelNumber: number): number {
  return Math.max(0.5, 1 - (d.cashRamp ?? 0) * Math.max(0, levelNumber - (d.cashRampFrom ?? 2)));
}

/**
 * Banens skærpelse: 1 på bane 1 og levelRamp mere for hver bane derfra (Orkan: 1,0 / 1,5 / 2,0 /
 * 2,5). Hændelsesbudgettet ganges med den, og forskuddet deles med den, så Orkan bliver strammere
 * bane for bane.
 */
export function levelRampOf(d: DifficultyDef, levelNumber: number): number {
  return 1 + d.levelRamp * (levelNumber - 1);
}

/** Grønt mål i procent for en bane på et givet niveau. */
export function greenGoal(levelGoalPct: number, id: DifficultyId): number {
  return Math.max(0, Math.min(100, levelGoalPct + DIFFICULTIES[id].greenGoalDeltaPp));
}
